import React from 'react';
import { Card, Badge, Button, Stack, Col, Row, Form } from 'react-bootstrap';
import { FaEdit, FaTrashAlt, FaCheckCircle, FaClone, FaEnvelope, FaHistory } from 'react-icons/fa';

const TransactionItem = ({ transaction, deleteTransaction, toggleComplete, handleSetEditing, cloneTransaction, handleShowLogs, runningBalance, isSelected, onToggleSelection }) => {
  const { 
    id, type, description, amount, date, accrual_date, category, subcategory,
    invoice_number, request_id, tax_origin, notes, status, entity_name, dap_interest, cc_commission, penalizaciones_booking, completion_date
  } = transaction;

  const isIncome = type === 'ingreso';
  const cardVariant = isIncome ? 'success' : 'danger';
  const isCompleted = status === 'completado';

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const transactionDate = new Date(date + 'T00:00:00');
  const isOverdue = !isCompleted && transactionDate < today;

  const getCardClassName = () => {
    if (isCompleted) return 'bg-light text-muted';
    if (isOverdue) return 'bg-danger-subtle';
    return '';
  };

  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString + 'T00:00:00').toLocaleDateString('es-CL', options);
  };

  const formatMonthYear = (dateString) => {
    if (!dateString) return '';
    const options = { year: 'numeric', month: 'long' };
    return new Date(dateString + 'T00:00:00').toLocaleDateString('es-CL', options);
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '';
    const options = { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' };
    return new Date(dateString).toLocaleString('es-CL', options);
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP' }).format(value || 0);
  };

  const generateMailtoLink = (transaction) => {
    const subject = `Recordatorio: Transacción Vencida - ${transaction.description}`;
    const body = `Estimado/a,\n\nLe escribimos para recordarle que la siguiente transacción se encuentra vencida:\n\n- Descripción: ${transaction.description}\n- Fecha: ${formatDate(transaction.date)}\n- Monto: ${formatCurrency(transaction.amount)}\n\nAgradeceríamos que pudiera revisar el estado de esta transacción a la brevedad posible.\n\nSaludos cordiales.`;
    return `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  const getEerrStatus = () => {
    if (!isCompleted && status !== 'incobrable') {
        return { text: 'Pendiente (No impacta EERR)', variant: 'light', textVariant: 'muted' };
    }

    const catLower = category ? category.toLowerCase() : '';
    if (['particulares', 'conductores'].includes(catLower)) {
        return { text: 'Excluido del EERR', variant: 'secondary' };
    }

    // Determine the effective date for EERR (Accrual > Date)
    const effectiveDateString = accrual_date || date;
    const effectiveDate = new Date(effectiveDateString + 'T00:00:00');
    const monthYear = effectiveDate.toLocaleDateString('es-CL', { month: 'short', year: 'numeric' });

    let attribution = '';
    let netImpact = 0;
    
    // Amount impact calculation
    // FIX: Local INCOMES do NOT add to EERR (Sales come from External Sheet).
    // Only EXPENSES add negative impact.
    if (type === 'egreso') {
        netImpact -= amount;
    } 
    // Note: For incomes, amount is ignored for EERR impact calculation.

    // Adjustments calculation
    if (dap_interest) netImpact += dap_interest;
    // Commissions and penalties are always expenses (negative impact)
    if (cc_commission) netImpact -= cc_commission;
    if (penalizaciones_booking) netImpact -= penalizaciones_booking;

    // Determine Attribution Category
    const subcatLower = (subcategory || "").toLowerCase().trim();
    if (catLower === 'movimiento financiero' && !subcatLower.includes('remesa')) {
        return { text: 'Solo Flujo Caja (No EERR)', variant: 'secondary' };
    }

    if (status === 'incobrable') {
        attribution = 'Castigo Deuda';
    } else if (['remuneraciones', 'salario'].includes(catLower)) {
        attribution = 'Remuneraciones';
    } else if (catLower.includes('hhee')) {
        attribution = 'HHEE';
    } else if (catLower === 'impuestos directos') {
        attribution = 'Impuestos';
    } else if (type === 'ingreso') {
        // If it has DAP interest, it's financial income. Otherwise, it's just cash flow.
        if (dap_interest > 0) attribution = 'Intereses DAP';
        else attribution = 'Ingreso (Flujo Caja)'; 
    } else {
        attribution = 'Gastos Op.'; // Everything else is Operating Expense
    }

    if (netImpact > 0) return { text: `Suma (+): ${attribution} (${monthYear})`, variant: 'success' };
    if (netImpact < 0) return { text: `Resta (-): ${attribution} (${monthYear})`, variant: 'danger' };
    
    // If netImpact is 0, check if it's an income that implies it's only Cash Flow
    if (type === 'ingreso' && netImpact === 0) {
         return { text: 'Solo Flujo Caja (No EERR)', variant: 'secondary' };
    }

    return { text: 'Neutro en EERR', variant: 'secondary' };
  };

  const eerrStatus = getEerrStatus();

  return (
    <Card border={cardVariant} className={getCardClassName()} id={`transaction-item-${id}`}>
      <Card.Header>
        <Stack direction="horizontal" gap={3} className="align-items-center">
          {onToggleSelection && (
            <Form.Check 
              type="checkbox" 
              checked={isSelected || false}
              onChange={() => onToggleSelection(id)}
              aria-label={`Seleccionar transacción ${id}`}
            />
          )}
          <span className="fw-bold">{entity_name || description}</span>
          <Badge 
            bg={
              isCompleted ? 'success' :
              status === 'Por Procesar' ? 'primary' :
              status === 'Esperando OC/HES' ? 'info' :
              isOverdue ? 'danger' : 'warning'
            } 
            className="ms-auto"
            text={
              status === 'Esperando OC/HES' || (!isCompleted && !isOverdue && status !== 'Por Procesar' && status !== 'Esperando OC/HES')
              ? 'dark' 
              : 'white'
            }
          >
            {
              isCompleted ? 'Completado' :
              status === 'Por Procesar' ? 'Por Procesar' :
              status === 'Esperando OC/HES' ? 'Esperando OC/HES' :
              isOverdue ? 'Vencida' : 'Pendiente'
            }
          </Badge>
          <Badge bg={isIncome ? 'success' : 'danger'}>
            {isIncome ? 'Ingreso' : 'Egreso'}
          </Badge>
          <Badge bg="secondary">{category}</Badge>
          {subcategory && <Badge bg="info" text="dark">{subcategory}</Badge>}
        </Stack>
      </Card.Header>
      <Card.Body>
        <Row>
          <Col md={8}>
            <Card.Text><strong>Monto:</strong> {formatCurrency(amount)}</Card.Text>
            <Card.Text><strong>Impacto en Saldo:</strong> {formatCurrency(transaction.balanceImpact)}</Card.Text>
            <Card.Text><strong>Fecha:</strong> {formatDate(date)}</Card.Text>
            {isCompleted && completion_date && <Card.Text><strong>Fecha Finalización:</strong> {formatDateTime(completion_date)}</Card.Text>}
            {entity_name && <Card.Text><strong>Empresa/Conductor:</strong> {entity_name}</Card.Text>}
            {description && <Card.Text><strong>Descripción:</strong> {description}</Card.Text>}
            {invoice_number && <Card.Text><strong>Boleta/Factura:</strong> {invoice_number}</Card.Text>}
            {request_id && <Card.Text><strong>Solicitud/Liquidación:</strong> {request_id}</Card.Text>}
            {tax_origin && <Card.Text><strong>Origen Impuesto:</strong> {tax_origin}</Card.Text>}
            {dap_interest > 0 && <Card.Text><strong>Intereses DAP:</strong> {formatCurrency(dap_interest)}</Card.Text>}
            {cc_commission > 0 && <Card.Text><strong>Comisión T/C:</strong> {formatCurrency(cc_commission)}</Card.Text>}
            {notes && <Card.Text><strong>Observaciones:</strong> {notes}</Card.Text>}
          </Col>
          <Col md={4} className="d-flex flex-column align-items-end justify-content-between">
            <div className="text-end mb-2">
                {accrual_date && (
                    <div className="text-muted small mb-1" title="Fecha de Imputación">
                        F. Imputación: {formatMonthYear(accrual_date)}
                    </div>
                )}
                {typeof runningBalance === 'number' && <Card.Text><strong>Saldo Acumulado:</strong> {formatCurrency(runningBalance)}</Card.Text>}
            </div>
            
            <Stack gap={2} className="align-items-end">
                <div className="d-flex gap-2">
                    <Button variant="outline-info" size="sm" title="Ver Historial" onClick={() => handleShowLogs(id)}>
                      <FaHistory />
                    </Button>
                    {isOverdue && (
                      <Button as="a" href={generateMailtoLink(transaction)} variant="outline-warning" size="sm" title="Enviar Mail">
                        <FaEnvelope />
                      </Button>
                    )}
                    {isCompleted ? (
                        <Badge bg="success" className="p-2">Completada</Badge>
                    ) : (
                        <Button variant="outline-success" size="sm" title="Marcar como completada" onClick={() => toggleComplete(id)}>
                            <FaCheckCircle />
                        </Button>
                    )}
                    <Button variant="outline-secondary" size="sm" title="Clonar" onClick={() => cloneTransaction(id)}>
                        <FaClone />
                    </Button>
                    <Button variant="outline-primary" size="sm" title="Editar" onClick={() => handleSetEditing(transaction)}>
                        <FaEdit />
                    </Button>
                    <Button variant="outline-danger" size="sm" title="Eliminar" onClick={() => deleteTransaction(id)}>
                        <FaTrashAlt />
                    </Button>
                </div>
            </Stack>
          </Col>
        </Row>
      </Card.Body>
      <Card.Footer className="text-muted text-start py-1 d-flex justify-content-between align-items-center">
        <small>ID: {id}</small>
        <Badge bg={eerrStatus.variant} text={eerrStatus.textVariant || 'white'} className="border">
            {eerrStatus.text}
        </Badge>
      </Card.Footer>
    </Card>
  );
};

export default TransactionItem;
